import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from '@/lib/api/client';
import { teamKeys } from '@/hooks/api/query-keys';
import { useInvalidatePermissions } from '@/hooks/use-permissions';
import { roleKeys } from '@/lib/roles/api';

// ============ Types ============

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: 'Company Owner' | 'Company Admin' | 'Workspace Admin' | 'Workspace Editor' | 'Workspace Viewer';
  status: 'Active' | 'Invited' | 'Deactivated';
  lastActive: string | null;
  avatarUrl: string | null;
  /** The custom role this member holds in the workspace (2026-10-05); null = the built-in `role`. */
  customRole?: { id: string; name: string; artworkKey: string | null } | null;
}

/** Shape returned by GET /v1/team/invites */
export interface TeamInvite {
  id: string;
  email: string;
  status: 'Pending' | 'Accepted' | 'Expired' | 'Revoked';
  createdAt: string;
  expiresAt: string;
  resendCount: string;
  /** Set by POST /team/invites when an open invite already existed and the
   *  create was treated as a resend (token rotated on the same invite). */
  resent?: boolean;
}

// Shared query key for the invite list — kept in sync with pending-invites.tsx
export const TEAM_INVITES_QUERY_KEY = ['team-invites'] as const;

// ============ Query Hooks ============

/**
 * Page size for the team list.
 *
 * 500 is `PaginationDto`'s documented maximum (common/dto/pagination.dto.ts),
 * and it is passed EXPLICITLY because that DTO defaults `limit` to 10. A bare
 * `/users` therefore returned the first ten members and nothing else — on
 * production, Infosec Ventures listed 10 of its 35 users and both stat cards
 * reported 10 as the total, because `GET /v1/users` maps `result.data` to a
 * plain array and drops `total`, so the page cannot tell a page from the whole
 * set (2026-09-17). A capped row fetch produces wrong NUMBERS, not a slow page.
 *
 * Known ceiling: a company with more than 500 admin users would truncate again.
 * Fixing that properly means returning the total from the endpoint rather than
 * raising a constant, and is tracked separately.
 */
const TEAM_MEMBERS_PAGE_SIZE = 500;

export function useTeamMembers() {
  return useQuery({
    queryKey: teamKeys.lists(),
    queryFn: () =>
      fetchApi<TeamMember[]>(`/users?limit=${TEAM_MEMBERS_PAGE_SIZE}`),
  });
}

// ============ Mutation Hooks ============

// FIX: Invite must call POST /team/invites (generates token + sends email via
// MailService.sendInvitationEmail). The old POST /users created a user record
// directly with no email sent — the invitee never received a link.
export function useInviteTeamMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { email: string; role: TeamMember['role'] }) =>
      fetchApi<TeamInvite>('/team/invites', {
        method: 'POST',
        // SECURITY (C-03): send the role the admin actually selected. Previously
        // only { email } was sent and the BE silently stored 'Admin' for every
        // invite — so a "Viewer" invite granted Admin. The BE now maps this role
        // label to its company-role enum and defaults to least-privilege.
        body: JSON.stringify({ email: data.email, role: data.role }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.all });
      queryClient.invalidateQueries({ queryKey: TEAM_INVITES_QUERY_KEY });
    },
  });
}

export function useUpdateTeamMemberRole() {
  const queryClient = useQueryClient();
  // The grants live in the ROOT client (usePermissions), which a module's own QueryProvider hides.
  const invalidatePermissions = useInvalidatePermissions();
  return useMutation({
    // Role lives on the dedicated PATCH /users/:id/role (ChangeRoleDto) route.
    // The generic PATCH /users/:id (UpdateUserDto) whitelists only
    // firstName/lastName/isActive, so sending { role } there 400s with
    // forbidNonWhitelisted (QA 2026-06-10 — was a silent failure).
    //
    // `customRoleId` gives a custom role (the API then stores the member under the role's tier,
    // whatever `role` says); null or absent means the built-in `role` and clears any custom one.
    mutationFn: ({ id, role, customRoleId = null }: { id: string; role: TeamMember['role']; customRoleId?: string | null }) =>
      fetchApi<TeamMember>(`/users/${id}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role, customRoleId }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.all });
      // The role cards count their holders, and a person may have changed their own access.
      queryClient.invalidateQueries({ queryKey: roleKeys.all() });
      void invalidatePermissions();
    },
  });
}

export function useDeactivateTeamMember() {
  const queryClient = useQueryClient();
  return useMutation({
    // UpdateUserDto whitelists `isActive` (boolean) — not `status`. Sending
    // { status:'Deactivated' } 400s under forbidNonWhitelisted (QA 2026-06-10).
    mutationFn: (id: string) =>
      fetchApi<TeamMember>(`/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: false }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.all });
    },
  });
}

// FIX: Resend invite for a member row in the team table. Looks up the pending
// invite by member email from the cached invite list, then calls
// POST /team/invites/:id/resend which issues a new token + sends email.
// The old hook called POST /users/:id/resend-invite which had a TODO comment
// and never actually sent any email.
export function useResendInviteByEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (memberEmail: string) => {
      // Pull invite list from cache (populated by PendingInvites component or
      // the GET /team/invites query). Fall back to a live fetch if not cached.
      const cached = queryClient.getQueryData<TeamInvite[]>(TEAM_INVITES_QUERY_KEY);
      const invites: TeamInvite[] = cached ?? await fetchApi<TeamInvite[]>('/team/invites');
      const invite = invites.find(
        (inv) => inv.email.toLowerCase() === memberEmail.toLowerCase() &&
          (inv.status === 'Pending' || inv.status === 'Expired'),
      );
      if (!invite) {
        // Names the section as it is LABELLED on screen. It said "Pending
        // Invitations" until that heading was corrected — the list shows
        // accepted and revoked invites too — and an error that points at a
        // heading which no longer exists is worse than no pointer at all.
        throw new Error('No open invite found for this member. Try the Invitations section below.');
      }
      return fetchApi<{ message: string }>(`/team/invites/${invite.id}/resend`, { method: 'POST' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEAM_INVITES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: teamKeys.all });
    },
  });
}
